import { Card, CardBody, CardHeader } from '@progress/kendo-react-layout';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { Button } from '@progress/kendo-react-buttons';
import * as React from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  ApplicationDownPaymentResponse,
  ColumnInfo,
  GenericGrid,
  hasValue,
  toDollars
} from '@amsanalytics/react-shared-library';
import {getTotalDownPaymentAmountPaid} from "../../../utils/utils";
import {GridRowProps} from "@progress/kendo-react-grid";
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import BlockIcon from '@mui/icons-material/Block';
import {ApplicationDispatch, SET_LOADING, voidDownPayment} from "../../../store/application/types";
import {useDispatch, useSelector} from "react-redux";
import {useDialog} from "../../../providers/DialogProvider";
import ConfirmationDialog from "../../Dialogs/ConfirmationDialog";
import {IEmployee} from "../../../Interfaces/Admin/IEmployee";
import {AppState} from "../../../store/rootReducer";
import ErrorDialog from "../../Dialogs/ErrorDialog";
import { IConfiguration } from '../../../Data/ConfigurationData';
import { AmsUserLookup } from '../../../Interfaces/Common/AmsUserLookup';
import { PaymentSource } from '../../../Interfaces/Common/PaymentSource';
import { ILookupItems } from '../../../Interfaces/ILookupItems';
import { useDownPaymentCardCharges } from '../../../Data/DownPaymentCardData';
import { chargesToActOn, depositCardLabel } from '../../../payments/downPaymentCards';
import { CardPaymentsWindow } from './CardPayments/CardPaymentsWindow';
import { viewDownPaymentReceiptReportAsync } from '../../../Data/DownPaymentReceiptData';

export interface DownPaymentHistoryCardProps {
  postedDate: Date | null;
  applicationDownPaymentResponses: ApplicationDownPaymentResponse[];
  configuration: IConfiguration,
}

// The application's down payments (deposits AMS has) and, for card down payments, the card that paid each one.
export const DownPaymentHistoryCard: React.FC<DownPaymentHistoryCardProps> = (props) => {
  const activeUser: IEmployee = useSelector((state: AppState) => state.applications.loggedInUser!.employee);
  const lookups: ILookupItems = useSelector((state: AppState) => state.applications.lookups);
  const applicationId = useSelector((state: AppState) => state.applications.editingApp?.id);
  const dispatch = useDispatch<ApplicationDispatch>();
  const [openDialog, closeDialog] = useDialog();
  const configuration = props.configuration;
  // LOS card down payments A13: the card rows label their deposits and may need action under Card payments.
  const { data: cardCharges } = useDownPaymentCardCharges(applicationId);
  const [showCardPayments, setShowCardPayments] = React.useState<boolean>(false);
  const cardRowsToActOn = chargesToActOn(cardCharges);

  const getAmsUserName = (amsUserId: string | null): string => {
    if (!amsUserId) return '';
    const user: AmsUserLookup | undefined = lookups.amsUsers.find(u => u.id === amsUserId);
    return user ? `${user.firstName} ${user.lastName}`.trim() : '';
  };

  const getPaymentSourceDescription = (paymentSourceId: string | null): string => {
    if (!paymentSourceId) return '';
    const source: PaymentSource | undefined = lookups.paymentSources.find(ps => ps.id === paymentSourceId);
    return source?.description ?? '';
  };

  const openVoidDownPaymentDialogAsync = async (applicationDownPaymentResponse: ApplicationDownPaymentResponse) => {
    if (hasValue(props.postedDate)) {
      openDialog({
        children: (
          <ErrorDialog
            title="Already Posted"
            errors={["You can't void a payment for a loan that has already been posted."]}
            closeDialog={() => {
              closeDialog();
            }}
          />
        ),
      });
    } else if (!hasValue(activeUser.employeeNumber)) {
      openDialog({
        children: (
          <ErrorDialog
            title="Employee Number"
            errors={['You must have an AMS 2000 employee associated with your account to void a down payment.']}
            closeDialog={() => {
              closeDialog();
            }}
          />
        ),
      });
    } else {
      openDialog({
        fullScreen: false,
        children: (
          <ConfirmationDialog
            title="Void Down Payment"
            prompt={`Do you want to void this down payment for ${toDollars(applicationDownPaymentResponse.amount)}?`}
            accept={() => {
              closeDialog();
              dispatch(voidDownPayment(applicationDownPaymentResponse.id));
            }}
            closeDialog={() => {
              closeDialog();
            }}
          />
        )
      });
    }
  };
  
  const ActionsCell = (props: GridRowProps) => {
    return (
      <td style={{ cursor: `pointer`, pointerEvents: `all` }}>
        <Tooltip anchorElement="target" position="top" parentTitle={true}>
          <Button
            // Icon-only: the icon-button class gives square padding, so both icons sit side by side.
            className="k-icon-button"
            fillMode="flat"
            size="small"
            themeColor="primary"
            title='Print Receipt'
            aria-label='Print Receipt'
            onClickCapture={async () => {
              dispatch({ type: SET_LOADING, payload: true });
              await viewDownPaymentReceiptReportAsync(configuration, props.dataItem.id);
              dispatch({ type: SET_LOADING, payload: false });
            }}
          >
            <ReceiptLongIcon />
          </Button>
          <Button
            className="k-icon-button"
            fillMode="flat"
            size="small"
            themeColor="primary"
            title='Void'
            aria-label='Void'
            onClickCapture={async () => {
              await openVoidDownPaymentDialogAsync(props.dataItem);
            }}
          >
            <BlockIcon />
          </Button>
        </Tooltip>
      </td>
    );
  };

  type DepositRow = ApplicationDownPaymentResponse & { amsUserName: string; paymentSourceDescription: string; cardLabel: string };

  const rows: DepositRow[] = (props.applicationDownPaymentResponses as ApplicationDownPaymentResponse[]).map(dpr => {
    return {
      ...dpr,
      cashDrawerDate: hasValue(dpr.cashDrawerDate) ? new Date(dpr.cashDrawerDate!) : null,
      amsUserName: getAmsUserName(dpr.amsUserId),
      paymentSourceDescription: getPaymentSourceDescription(dpr.paymentSourceId),
      // A13: "Visa ending 1111" for a deposit a card down payment made.
      cardLabel: depositCardLabel(dpr as ApplicationDownPaymentResponse & { transactionId?: string | null }, cardCharges) ?? '',
    };
  });

  let columnsToDisplayForUser: ColumnInfo[] = [
    { title: "Amount", field: "amount", format: '{0:c}' },
    { title: "Receipt #", field: "receipt" },
    { title: "User", field: "amsUserName" },
    { title: "Payment Source", field: "paymentSourceDescription" },
    // Widths keep a card row on one line: the label unwrapped and the two action icons side by side.
    ...(rows.some(row => row.cardLabel) ? [{ title: "Card", field: "cardLabel", width: 160 }] : []),
    { title: "Actions", width: 100, cell: (props: any) => <ActionsCell {...props}/>}
  ];

  const totalAmountPaid = getTotalDownPaymentAmountPaid(props.applicationDownPaymentResponses);

  return (
    <Card style={{ backgroundColor: 'inherit', maxHeight: "200px", margin: '5px 0'}}>
      <CardHeader style={{ background: '#ffc800', padding: '0.2rem .8rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 32 }}>
          <InfoOutlinedIcon />
          <span style={{ flex: 1 }}>{`Down Payments - ${toDollars(totalAmountPaid)} Paid`}</span>
          {cardRowsToActOn.length > 0 &&
            <Button size="small" onClick={() => setShowCardPayments(true)}>Card payments</Button>
          }
        </div>
      </CardHeader>
      <CardBody style={{ padding: '10px', maxHeight: "180px", overflowY: 'auto' }}>
        <GenericGrid<DepositRow>
          style={{ display: 'flex', flex: 1 }}
          columns={columnsToDisplayForUser}
          items={rows}
          disableAdding
          disableEditing
          disableExcelExporting
          disablePdfExporting
          disableFiltering
          disableGrouping
          size={"small"}
        />
      </CardBody>
      {showCardPayments && applicationId &&
        <CardPaymentsWindow applicationId={applicationId} onClose={() => setShowCardPayments(false)} />
      }
    </Card>
  );
}
